import {
	faCircleExclamation,
	faFilePdf,
	faImage,
} from '@fortawesome/pro-solid-svg-icons';
import type { MediaAssetDto } from '@supply-mover-2-0/shared';
import React, { useState } from 'react';

import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';
import { LoadingSpinner } from '../display/LoadingSpinner';

export interface MediaThumbnailProps {
	asset: Pick<
		MediaAssetDto,
		'mediaType' | 'status' | 'title' | 'altText' | 'urls' | 'focalX' | 'focalY'
	>;
	/** `cover` fills the box (grids); `contain` shows the whole picture (detail). */
	fit?: 'cover' | 'contain';
	className?: string;
	/** The address to show instead of the preview size, e.g. the main file in the detail panel. */
	src?: string | null;
}

/**
 * PD-16339 — a media item's picture as a grid tile or preview: the preview size for
 * pictures and SVG logos (cropped around the item's focal point when filling a box), a
 * PDF mark for documents, and the item's state while it is checked or when it failed.
 *
 * The box sets the size; the thumbnail fills it. Public CDN addresses, so a plain
 * `<img>` with lazy loading is enough (no signed-address fetch).
 */
export function MediaThumbnail({
	asset,
	fit = 'cover',
	className,
	src,
}: MediaThumbnailProps): React.ReactElement {
	const { t } = useTranslation();
	const [broken, setBroken] = useState(false);
	const address = src ?? asset.urls?.preview ?? null;

	const frame = cn(
		'relative flex items-center justify-center overflow-hidden',
		// An SVG logo is drawn for a light page (a website's header or a logo reel), and one
		// with dark lettering all but disappears on the dark theme's tile. White in both themes
		// shows it the way the website will.
		asset.mediaType === 'svg' ? 'bg-white' : 'bg-sunken',
		className,
	);

	if (asset.status === 'uploading' || asset.status === 'processing') {
		return (
			<div className={frame}>
				<LoadingSpinner size="sm" label={t('Checking the file...')} showLabel />
			</div>
		);
	}

	if (asset.status === 'failed') {
		return (
			<div className={cn(frame, 'flex-col gap-1 text-danger')}>
				<Icon icon={faCircleExclamation} size="lg" />
				<span className="text-xs">{t('Could not be used')}</span>
			</div>
		);
	}

	if (asset.mediaType === 'document' || address == null || broken) {
		return (
			<div className={cn(frame, 'flex-col gap-1 text-muted')}>
				<Icon
					icon={asset.mediaType === 'document' ? faFilePdf : faImage}
					size="2x"
				/>
				{asset.mediaType === 'document' && (
					<span className="text-xs">{t('PDF')}</span>
				)}
			</div>
		);
	}

	const focal =
		fit === 'cover' && asset.focalX != null && asset.focalY != null
			? `${Math.round(asset.focalX * 100)}% ${Math.round(asset.focalY * 100)}%`
			: undefined;

	return (
		<div className={frame}>
			<img
				src={address}
				alt={t(asset.altText ?? asset.title)}
				loading="lazy"
				decoding="async"
				onError={() => {
					setBroken(true);
				}}
				className={cn(
					'h-full w-full',
					fit === 'cover' ? 'object-cover' : 'object-contain',
				)}
				style={focal != null ? { objectPosition: focal } : undefined}
			/>
		</div>
	);
}
